import { useState, type ReactNode } from 'react'
import {
  CURRENCY_SUFFIX,
  formatAmount,
  formatDate,
  formatMoney,
  isoDay,
  nowMs,
  parseIsoDay,
  withCountRu,
} from '@dorixona/utils'
import { PageActions } from '../../app/PageHeader'
import { Icon } from '../../ui/Icon'
import { DatePicker } from '../../ui/DatePicker'
import { Failed } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useStores } from '../stores/api'
import { storeTitleById } from '../stores/model'
import { RevenueChart } from './RevenueChart'
import { TopPanel } from './TopPanel'
import { useDashboard, type Totals } from './api'
import { dashRange, deltaPercent, rangeQuery, toBars, toRows } from './model'

const PRESETS: [number, string][] = [
  [7, '7 дней'],
  [14, '14 дней'],
  [30, '30 дней'],
]

const ORDERS: [string, string, string] = ['заказ', 'заказа', 'заказов']

/** Сумма плитки: число крупно, «сум» мелко — валюта одна на весь экран. */
function Money({ value }: { value: number }) {
  return (
    <>
      {formatAmount(value)}
      <small>{CURRENCY_SUFFIX}</small>
    </>
  )
}

/**
 * Сравнение с прошлым периодом. Ноль — не рост: без знака и без цвета,
 * иначе ровный показатель зеленел бы как успех.
 */
function Delta({ current, previous }: { current: number; previous: number }) {
  const percent = deltaPercent(current, previous)
  if (percent === 0) return <span className="delta">0% к прошлому периоду</span>
  const up = percent > 0
  return (
    <span className="delta" data-dir={up ? 'up' : 'down'}>
      <b>
        {up ? '+' : '−'}
        {Math.abs(percent)}%
      </b>{' '}
      к прошлому периоду
    </span>
  )
}

function Tile({
  label,
  value,
  children,
}: {
  label: ReactNode
  value: ReactNode
  children: ReactNode
}) {
  return (
    <div className="ui-tile">
      <span className="lbl">{label}</span>
      <span className="val">{value}</span>
      {children}
    </div>
  )
}

function Tiles({
  totals,
  compare,
  label,
  storeName,
}: {
  totals: Totals
  compare: Totals
  label: string
  storeName: string
}) {
  const orders = totals.ordersPickup + totals.ordersDelivery
  return (
    <div className="dsh-tiles">
      <Tile label={`Выручка всего · ${label}`} value={<Money value={totals.revenueTotal} />}>
        <Delta current={totals.revenueTotal} previous={compare.revenueTotal} />
      </Tile>
      <Tile label="Онлайн-заказы" value={<Money value={totals.revenueOnline} />}>
        <span className="delta">{withCountRu(orders, ORDERS)}</span>
      </Tile>
      <Tile
        label={
          <>
            Офлайн-касса <span className="ui-tag">FOM</span>
          </>
        }
        value={<Money value={totals.revenueOffline} />}
      >
        <Delta current={totals.revenueOffline} previous={compare.revenueOffline} />
      </Tile>
      {/* Кэшбэк — обычное число: жёлтый в админке только у очереди. */}
      <Tile label="Кэшбэк начислен" value={<Money value={totals.cashbackGiven} />}>
        <Delta current={totals.cashbackGiven} previous={compare.cashbackGiven} />
      </Tile>
      <Tile label="Самовывоз" value={totals.ordersPickup}>
        <Delta current={totals.ordersPickup} previous={compare.ordersPickup} />
      </Tile>
      <Tile label="Доставка" value={totals.ordersDelivery}>
        <Delta current={totals.ordersDelivery} previous={compare.ordersDelivery} />
      </Tile>
      <Tile label="Средний чек онлайн" value={orders ? <Money value={totals.averageOrder} /> : '—'}>
        <span className="delta">списано баллами {formatMoney(totals.pointsSpent)}</span>
      </Tile>
      {storeName ? (
        <Tile label="Заказов в работе" value={totals.ordersInWork}>
          <span className="delta">{storeName}</span>
        </Tile>
      ) : (
        <Tile label="Новые клиенты" value={totals.newCustomers}>
          <span className="delta">{withCountRu(totals.ordersInWork, ORDERS)} в работе</span>
        </Tile>
      )}
    </div>
  )
}

/** Загрузка — макет плиток на их месте: экран не прыгает, когда цифры приходят. */
function TilesSkeleton() {
  return (
    <div className="dsh-tiles" role="status">
      <span className="visually-hidden">Загружаем показатели…</span>
      {Array.from({ length: 8 }, (_, i) => (
        <div className="ui-tile" key={i} aria-hidden="true">
          <span className="ui-skeleton dsh-skel-lbl" />
          <span className="ui-skeleton dsh-skel-val" />
          <span className="ui-skeleton dsh-skel-delta" />
        </div>
      ))}
    </div>
  )
}

/**
 * Дашборд прототипа: показатели периода, выручка по дням и «топы».
 * Все цифры считает сервер (`GET /admin/dashboard`) — так админка и отчёты
 * не расходятся. Сотрудник аптеки видит только свою точку: сервер сузит
 * выборку сам, а метка рядом с периодом объясняет, почему цифры не сетевые (В-38).
 */
export function DashboardScreen() {
  const { storeId, isSuper } = useSession()
  const [now] = useState(() => nowMs())
  const [days, setDays] = useState(7)
  const [from, setFrom] = useState<number | null>(null)
  const [to, setTo] = useState<number | null>(null)
  // «Свой период» открывает поля дат, но запрос меняет только «Применить»:
  // пока даты набираются, на экране остаются цифры прежнего периода.
  const [picking, setPicking] = useState(false)
  const [error, setError] = useState('')

  const scope = isSuper ? 0 : storeId
  const range = dashRange(now, days, from, to)
  const query = rangeQuery(range)
  const label = range.custom
    ? `${formatDate(range.from)} — ${formatDate(range.to)}`
    : `${range.days} дн.`
  const custom = picking || range.custom

  const dashboard = useDashboard({ ...query, storeId: scope })
  // Название своей аптеки берём из справочника: в ответе дашборда его нет.
  const stores = useStores({ enabled: scope !== 0 })
  const storeName = scope ? storeTitleById(stores.data, scope, '') : ''

  const [fromField, setFromField] = useState(isoDay(range.from))
  const [toField, setToField] = useState(isoDay(range.to))

  function preset(value: number) {
    setDays(value)
    setFrom(null)
    setTo(null)
    setPicking(false)
    setError('')
  }

  function apply() {
    const start = parseIsoDay(fromField)
    const end = parseIsoDay(toField)
    if (start === null || end === null) {
      setError('Укажите обе даты периода')
      return
    }
    if (end < start) {
      setError('Дата «по» раньше даты «с» — поменяйте местами')
      return
    }
    setError('')
    setFrom(start)
    setTo(end)
  }

  return (
    <div className="ui-dashboard">
      <PageActions>
        {storeName ? (
          <span className="dsh-scope">
            <Icon name="i-pin" size={16} />
            Статистика аптеки: {storeName}
          </span>
        ) : null}
        <span className="spacer" />
        <div className="ui-seg" role="group" aria-label="Период">
          {PRESETS.map(([value, text]) => (
            <button
              key={value}
              type="button"
              aria-pressed={!custom && days === value}
              onClick={() => {
                preset(value)
              }}
            >
              {text}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={custom}
            onClick={() => {
              setPicking(true)
            }}
          >
            Свой период
          </button>
        </div>
        {custom ? (
          <div className="dsh-range" role="group" aria-label="Свой период">
            <label htmlFor="dash-from">с</label>
            <DatePicker
              id="dash-from"
              label="Начало периода"
              value={fromField}
              max={isoDay(now)}
              invalid={error !== ''}
              describedBy={error ? 'dash-range-error' : undefined}
              onChange={setFromField}
            />
            <label htmlFor="dash-to">по</label>
            <DatePicker
              id="dash-to"
              label="Конец периода"
              value={toField}
              max={isoDay(now)}
              invalid={error !== ''}
              describedBy={error ? 'dash-range-error' : undefined}
              onChange={setToField}
            />
            <button type="button" className="ui-btn" onClick={apply}>
              Применить
            </button>
          </div>
        ) : null}
        {error ? (
          <p id="dash-range-error" className="ui-error dsh-range-error" role="alert">
            <Icon name="i-alert" size={14} />
            {error}
          </p>
        ) : null}
      </PageActions>

      {dashboard.isPending ? <TilesSkeleton /> : null}
      {dashboard.isError ? (
        <Failed
          what="показатели"
          error={dashboard.error}
          onRetry={() => {
            void dashboard.refetch()
          }}
        />
      ) : null}

      {dashboard.data ? (
        <>
          <Tiles
            totals={dashboard.data.totals}
            compare={dashboard.data.compare}
            label={label}
            storeName={storeName}
          />

          {/* Две независимые колонки, а не ряды: блоки разной высоты встают
              друг под другом, и под коротким графиком не остаётся дыры. */}
          <div className="dsh-grid">
            <div className="dsh-col">
              <section className="ui-card">
                <div className="ui-card-head">
                  <h3>Выручка онлайн · {label}</h3>
                </div>
                <div className="ui-card-body">
                  <RevenueChart bars={toBars(dashboard.data.revenueByDay)} />
                </div>
              </section>
              <TopPanel
                title={`${storeName ? 'Самовывоз в моей аптеке' : 'Топ аптек по самовывозу'} · ${label}`}
                rows={toRows(dashboard.data.topPharmaciesPickup, 'count')}
                empty="Заказов самовывозом за период нет"
                opens="карточку аптеки"
              />
            </div>
            <div className="dsh-col">
              <TopPanel
                title={`Топ товаров · ${label}`}
                rows={toRows(dashboard.data.topProducts, 'count')}
                empty="Продаж за период нет"
                opens="карточку товара"
              />
              <TopPanel
                title={`Топ категорий · ${label}`}
                rows={toRows(dashboard.data.topCategories, 'money')}
                empty="Продаж за период нет"
                unit="money"
                opens="категорию"
              />
              <TopPanel
                title={`Доставка по районам · ${label}`}
                rows={toRows(dashboard.data.deliveryByDistrict, 'count')}
                empty="Доставок за период нет"
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
